import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  RobotIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  LightningIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import { hasCustomerToken } from "@/lib/customerApi";
import { usePageView } from "@/lib/usePageView";
import HubFooter from "./HubFooter";
import StoreFaq from "./StoreFaq";
import BotArena from "./BotArena";
import StoreBackground from "./StoreBackground";
import { RiskNotice, SoftwareModel } from "./RiskNotice";

// Software-only home page: MT5 Expert Advisors. cTrader Copy is hidden (code kept for future use).
const HUB_NAV = [
  { id: "mt5", label: "MT5 bots", to: "/mt5", c: "#38BDF8" },
  { id: "pricing", label: "Pricing", to: "/pricing", c: "#FBBF24" },
  { id: "track", label: "Track record", to: "/track-record", c: "#34D399" },
  { id: "setup", label: "Setup guide", to: "/setup", c: "#D6FF38" },
  { id: "how", label: "How it works", href: "#how", c: "#E879F9" },
  { id: "faq", label: "FAQ", href: "#faq", c: "#22D3EE" },
];

function HubNavLink({ n, mobile, onClick, className }) {
  const props = { onClick, "data-testid": `hub-${mobile ? "mobile-" : ""}nav-${n.id}`, style: { color: n.c, borderColor: `${n.c}66`, background: `${n.c}1A` },
    className: `${className} whitespace-nowrap border hover:brightness-125 transition` };
  return n.to ? <Link to={n.to} {...props}>{n.label}</Link> : <a href={n.href} {...props}>{n.label}</a>;
}

function AccountLink({ testid, className }) {
  const on = hasCustomerToken();
  return (
    <Link to={on ? "/account" : "/account/login"} data-testid={testid}
      className={`${className} flex items-center gap-1.5 whitespace-nowrap bg-[#FBBF24] text-[#050608] font-semibold hover:bg-[#FDE68A] transition-colors`}>
      <UserCircleIcon size={16} weight="fill" /> {on ? "My account" : "Log in"}
    </Link>
  );
}

export default function Hub() {
  usePageView("/");
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <StoreBackground>
    <div
      data-testid="hub-page"
      className="relative min-h-screen bg-transparent text-[#F1F5F9] overflow-x-clip"
    >
      {/* topbar (background is now global via StoreBackground) */}
      <header className="sticky top-0 z-40 border-b border-[#1A1C24] backdrop-blur-md bg-[#050608]/90">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="hub-logo">
            <img src="/botgenomix-logo-512.png" alt="BotGenomix" className="h-12 w-12 rounded-lg" />
            <div>
              <div className="text-xl font-medium tracking-tight">
                Bot<span className="text-[#D6FF38]">Genomix</span>
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#64748B]">
                MT5 trading software
              </div>
            </div>
          </Link>
          <nav className="hidden xl:flex items-center gap-1.5 mono text-[11px] uppercase tracking-widest">
            {HUB_NAV.map((n) => <HubNavLink key={n.id} n={n} className="px-3 py-2 rounded-full" />)}
            <AccountLink testid="hub-nav-account" className="ml-3 px-4 py-2 rounded-full" />
          </nav>
          <div className="xl:hidden flex items-center gap-2">
            <AccountLink testid="hub-mobile-account" className="px-3 py-2 rounded-full mono text-[11px] uppercase tracking-widest" />
            <button
              data-testid="hub-mobile-toggle"
              aria-label="Toggle navigation"
              onClick={() => setMobileOpen((v) => !v)}
              className="p-2 rounded-lg border border-[#D6FF38]/40 bg-[#D6FF38]/10 text-[#D6FF38]"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {mobileOpen
                  ? (<><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>)
                  : (<><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></>)}
              </svg>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="xl:hidden border-t border-[#1A1C24] bg-[#050608]/95 backdrop-blur-md">
            <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col gap-2 mono text-[13px] uppercase tracking-widest">
              {HUB_NAV.map((n) => <HubNavLink key={n.id} n={n} mobile onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-lg text-center" />)}
            </div>
          </div>
        )}
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pt-20 pb-10">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#22D3EE]">
          MetaTrader 5 · Automated trading software
        </div>
        <h1 className="text-4xl md:text-6xl font-medium tracking-tight mt-4 max-w-5xl leading-[1.05]">
          Automated trading software,
          <br />
          <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-yellow-300 bg-clip-text text-transparent">
            tested and documented in the open.
          </span>
        </h1>
        <p className="text-lg text-[#94A3B8] mt-6 max-w-3xl leading-relaxed">
          BotGenomix develops, tests and licenses downloadable Expert Advisors for MetaTrader 5. An automated research
          pipeline builds and backtests thousands of rule-based strategies, and the ones that pass our published tests are
          listed with their full results. You choose, install and run the software yourself, on your own MT5 account.
        </p>

        <div className="mt-8 flex flex-wrap gap-3 mono text-[10px] uppercase tracking-widest">
          {[
            ["Downloadable .ex5 software", "#22D3EE"],
            ["Your own MT5 & broker", "#D6FF38"],
            ["Fixed, documented strategies", "#E879F9"],
            ["Cancel anytime", "#FBBF24"],
          ].map(([label, color]) => (
            <div
              key={label}
              className="border rounded-full px-3 py-1.5 flex items-center gap-2"
              style={{ borderColor: color + "40" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
              <span style={{ color }}>{label}</span>
            </div>
          ))}
        </div>
        <RiskNotice testid="hub-hero-risk" className="mt-8 max-w-4xl" />
      </section>

      {/* MT5 software */}
      <section id="choose" className="relative z-10 max-w-[1400px] mx-auto px-6 pb-16">
        <Link
          to="/mt5"
          data-testid="hub-tile-mt5"
          className="group relative block overflow-hidden border rounded-xl p-8 transition-all"
          style={{
            borderColor: "rgba(56,189,248,0.35)",
            background: "linear-gradient(135deg, rgba(30,58,138,0.35) 0%, rgba(202,138,4,0.15) 100%)",
          }}
        >
          <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-blue-500/25 blur-3xl group-hover:bg-blue-500/40 transition-colors" />
          <div className="relative grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#FBBF24]">
                <RobotIcon size={14} weight="duotone" />
                MetaTrader 5 · Expert Advisors
              </div>
              <h2 className="text-3xl font-medium tracking-tight mt-3 text-[#F1F5F9]">License the software.</h2>
              <p className="text-[#CBD5E1] mt-3 leading-relaxed">
                Subscribe to a software licence, download protected .ex5 Expert Advisors and install them in your own
                MetaTrader 5 terminal, on an account with a broker you choose.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#FBBF24] group-hover:gap-3 transition-all">
                View the MT5 software catalogue
                <ArrowRightIcon size={14} />
              </div>
              <div className="mt-4 block w-fit mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-[#FBBF24]">
                Pre-launch · purchases not open yet
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#CBD5E1]">
              {[
                "Monthly software licence - cancel any time",
                "Runs in your own MT5 terminal",
                "You control the settings, the account and whether it runs",
                "Source code is never shared - compiled .ex5 only",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-[#FBBF24]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </Link>
      </section>

      <SoftwareModel accent="#E879F9" />

      {/* Bot arena — illustrative animation of the testing pipeline */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pb-24">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#D6FF38]">
          The research process · illustration
        </div>
        <h2 className="text-3xl font-medium tracking-tight mt-2">Most strategies are rejected.</h2>
        <p className="text-sm text-[#94A3B8] mt-3 max-w-2xl">
          Every symbol is tested by many candidate strategies, and most fail our tests. This animation illustrates the
          process only - it does not show real trading or real results.
        </p>
        <div className="mt-8">
          <BotArena />
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing" className="scroll-mt-24 relative z-10 max-w-[1400px] mx-auto px-6 pb-24">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#FBBF24]">
          What it costs
        </div>
        <h2 className="text-3xl font-medium tracking-tight mt-2">
          Software licences, one flat monthly price. <span className="text-[#D6FF38]">No performance fees.</span>
        </h2>
        <p className="text-sm text-[#94A3B8] mt-3 max-w-2xl">
          Three monthly software-licence plans. The price doesn't depend on trading results. Month-rolling - cancel any time
          from Manage billing.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {[
            { name: "Single bot", price: 49, blurb: "A licence for any one listed bot.", tone: "#FBBF24" },
            { name: "Five bots", price: 149, blurb: "Licences for any five listed bots.", tone: "#D6FF38", save: "£96 less than 5 × Single" },
            { name: "Everything", price: 249, blurb: "Licences for every listed bot.", tone: "#FBBF24" },
          ].map((t) => (
            <div
              key={t.name}
              data-testid={`hub-tier-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="relative rounded-xl border p-5"
              style={{ borderColor: t.tone, background: `${t.tone}0F` }}
            >
              <div className="mono text-[10px] uppercase tracking-widest" style={{ color: t.tone }}>{t.name}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-3xl font-medium tracking-tight">£{t.price}</span>
                <span className="mono text-xs text-[#94A3B8]">/mo</span>
              </div>
              {t.save && (<div className="mono text-[10px] mt-1" style={{ color: t.tone }}>{t.save}</div>)}
              <p className="text-sm text-[#CBD5E1] mt-3">{t.blurb}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/pricing"
            data-testid="hub-see-full-pricing"
            className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg transition"
            style={{ background: "#D6FF38", color: "#050608" }}
          >
            See full price list <ArrowRightIcon size={12} weight="bold" />
          </Link>
          <Link
            to="/setup"
            data-testid="hub-pricing-to-setup"
            className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg border border-[#1A1C24] text-[#94A3B8] hover:text-[#F1F5F9] transition"
          >
            How setup works <ArrowRightIcon size={12} weight="bold" />
          </Link>
        </div>
      </section>

      {/* How we test */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pb-24 border-t border-[#1A1C24] pt-16">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#2ECA7F]">
          How results are shown
        </div>
        <h2 className="text-3xl font-medium tracking-tight mt-2">
          Every result labelled for what it is.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            { h: "Backtests are simulations", b: "Bot statistics in the catalogue come from the MetaTrader 5 strategy tester on historical data. They are simulated, produced with hindsight and were not achieved with real money." },
            { h: "Unseen-data test", b: "Listed bots are re-tested on 2025+ market data that wasn't used to build them. These results are also backtests, and are shown separately." },
            { h: "Demo account, not real money", b: "The Track record page shows the founder's demo account, which uses simulated funds. We don't currently publish verified live-money results." },
          ].map((c) => (
            <div key={c.h} className="border border-[#1A1C24] bg-[#0B0D14] rounded-lg p-6">
              <h3 className="font-medium text-lg text-[#F1F5F9]">{c.h}</h3>
              <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </section>

      <div id="faq" className="scroll-mt-24"><StoreFaq /></div>
      <section id="how" className="scroll-mt-24 relative z-10 max-w-[1400px] mx-auto px-6 pb-24 border-t border-[#1A1C24] pt-20">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#D6FF38]">
          From research to your terminal
        </div>
        <h2 className="text-3xl font-medium tracking-tight mt-2">
          How it works.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            {
              icon: <LightningIcon size={22} weight="duotone" />,
              color: "#22D3EE",
              title: "Automated research",
              body: "An AI-assisted pipeline generates and backtests rule-based strategies on real MT5 tick data. Most are rejected.",
            },
            {
              icon: <ShieldCheckIcon size={22} weight="duotone" />,
              color: "#2ECA7F",
              title: "Published testing",
              body: "Before listing, bots are re-tested with doubled spreads, adjusted settings and 2025+ data they never saw. Results are published, including the weak ones.",
            },
            {
              icon: <RobotIcon size={22} weight="duotone" />,
              color: "#FBBF24",
              title: "You install and run it",
              body: "Download the .ex5, install it in your own MT5 terminal, enter your licence key and decide whether to switch it on.",
            },
          ].map((c) => (
            <div
              key={c.title}
              className="relative border border-[#1A1C24] bg-[#0B0D14] rounded-lg p-6"
            >
              <div style={{ color: c.color }}>{c.icon}</div>
              <h3 className="mt-3 font-medium text-lg text-[#F1F5F9]">{c.title}</h3>
              <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <HubFooter />
    </div>
    </StoreBackground>
  );
}
